import { useEffect, useMemo } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { useFeatureStore } from '../store/featureStore';
import { GIT_PROVIDERS, GitProvider, isProviderEnabled } from '../config/providers';

/**
 * The git providers the UI should offer, in display order.
 *
 * Providers without a `featureKey` are always on. Gated providers (e.g. AWS CodeCommit)
 * appear only once `/features/effective` reports their key. This reads the shared
 * feature store (the same one `useFeatures` wraps) and loads it once if nothing has
 * fetched it yet, so leaf components can call this freely without issuing a request
 * on every mount.
 */
export const useEnabledProviders = (): GitProvider[] => {
  const api = useAuthenticatedApi();
  const features = useFeatureStore((s) => s.features);
  const loading = useFeatureStore((s) => s.loading);
  const error = useFeatureStore((s) => s.error);
  const lastFetchedAt = useFeatureStore((s) => s.lastFetchedAt);
  const load = useFeatureStore((s) => s.load);

  useEffect(() => {
    if (api && lastFetchedAt === null && !loading && !error) {
      load(api);
    }
  }, [api, lastFetchedAt, loading, error, load]);

  // Key the memo on the resolved set (not the features array identity) so consumers that
  // put the result in effect deps do not re-run when the store reloads with the same answer.
  const enabledKey = GIT_PROVIDERS.filter((p) => isProviderEnabled(p, features)).join(',');
  return useMemo(() => enabledKey.split(',').filter(Boolean) as GitProvider[], [enabledKey]);
};
